"use client"

import { useEffect, useMemo, useState, useRef, useCallback } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Navbar from "../../components/account/Navbar"
import api from "../../services/api"
import "./accountmanage.css"

const PrivacySettingsContent = () => {
  const navigate = useNavigate()
  const { userType } = useParams()
  const { t, i18n } = useTranslation()
  const isRTL = i18n.dir() === "rtl"

  const [privacySettings, setPrivacySettings] = useState({
    showLastTimeSeen: true,
    showReadReceipt: true,
    acceptAudioVideoCalls: true,
    whoCanChatDirectly: "",
    whoCanSeeMyPosts: "",
    whoCanSendFollowRequests: "",
    whoCanFindMeOnSearch: "",
    whoCanSeeMyBuddies: "",
  })

  const [isLoading, setIsLoading] = useState(false)
  const [saveStatus, setSaveStatus] = useState(null)
  const [isLoadingSettings, setIsLoadingSettings] = useState(true)
  const isInitialLoad = useRef(true)
  const saveTimeoutRef = useRef(null)

  const privacyOptionLabels = useMemo(
    () => ({
      friends: t("privacySettings.options.friends"),
      friends_of_friends: t("privacySettings.options.friends_of_friends"),
      everyone: t("privacySettings.options.everyone"),
      no_one: t("privacySettings.options.no_one"),
    }),
    [t]
  )

  const privacyOptions = useMemo(
    () => [
      { value: "friends", label: privacyOptionLabels.friends },
      { value: "friends_of_friends", label: privacyOptionLabels.friends_of_friends },
      { value: "everyone", label: privacyOptionLabels.everyone },
      { value: "no_one", label: privacyOptionLabels.no_one },
    ],
    [privacyOptionLabels]
  )

  const chatToggleItems = useMemo(
    () => [
      {
        key: "showLastTimeSeen",
        label: t("privacySettings.toggles.showLastTimeSeen.label"),
        description: t("privacySettings.toggles.showLastTimeSeen.description"),
      },
      {
        key: "showReadReceipt",
        label: t("privacySettings.toggles.showReadReceipt.label"),
        description: t("privacySettings.toggles.showReadReceipt.description"),
      },
      {
        key: "acceptAudioVideoCalls",
        label: t("privacySettings.toggles.acceptAudioVideoCalls.label"),
        description: t("privacySettings.toggles.acceptAudioVideoCalls.description"),
      },
    ],
    [t]
  )

  const dropdownItems = useMemo(
    () => [
      {
        key: "whoCanChatDirectly",
        label: t("privacySettings.dropdowns.whoCanChatDirectly.label"),
        description: t("privacySettings.dropdowns.whoCanChatDirectly.description"),
      },
      {
        key: "whoCanSeeMyPosts",
        label: t("privacySettings.dropdowns.whoCanSeeMyPosts.label"),
        description: t("privacySettings.dropdowns.whoCanSeeMyPosts.description"),
      },
      {
        key: "whoCanSendFollowRequests",
        label: t("privacySettings.dropdowns.whoCanSendFollowRequests.label"),
        description: t("privacySettings.dropdowns.whoCanSendFollowRequests.description"),
      },
      {
        key: "whoCanFindMeOnSearch",
        label: t("privacySettings.dropdowns.whoCanFindMeOnSearch.label"),
        description: t("privacySettings.dropdowns.whoCanFindMeOnSearch.description"),
      },
      {
        key: "whoCanSeeMyBuddies",
        label: t("privacySettings.dropdowns.whoCanSeeMyBuddies.label"),
        description: t("privacySettings.dropdowns.whoCanSeeMyBuddies.description"),
      },
    ],
    [t]
  )

  useEffect(() => {
    const loadPrivacySettings = async () => {
      try {
        setIsLoadingSettings(true)
        const response = await api.get("/api/v1/privacy-settings")

        if (response.data) {
          const settings = response.data
          setPrivacySettings({
            showLastTimeSeen: settings.show_last_time_seen ?? true,
            showReadReceipt: settings.show_read_receipts ?? true,
            acceptAudioVideoCalls: settings.allow_others_to_call ?? true,
            whoCanChatDirectly: settings.who_can_send_messages || "",
            whoCanSeeMyPosts: settings.who_can_see_my_posts || "",
            whoCanSendFollowRequests: settings.who_can_send_me_follow_requests || "",
            whoCanFindMeOnSearch: settings.who_can_find_me_on_search || "",
            whoCanSeeMyBuddies: settings.who_can_see_my_buddies || "",
          })
        }
      } catch (error) {
        console.error("Error loading privacy settings:", error)
      } finally {
        setIsLoadingSettings(false)
        // Mark initial load as complete after a short delay to avoid saving on load
        setTimeout(() => {
          isInitialLoad.current = false
        }, 500)
      }
    }

    loadPrivacySettings()
  }, [])

  // Auto-save effect with debouncing
  useEffect(() => {
    // Don't auto-save on initial load
    if (isInitialLoad.current || isLoadingSettings) {
      return
    }

    // Validate that all required dropdown fields have values before auto-saving
    const requiredFields = [
      "whoCanChatDirectly",
      "whoCanSeeMyPosts",
      "whoCanSendFollowRequests",
      "whoCanFindMeOnSearch",
      "whoCanSeeMyBuddies",
    ]

    const hasAllRequiredFields = requiredFields.every(
      (field) => privacySettings[field] && privacySettings[field] !== ""
    )

    if (!hasAllRequiredFields) {
      // Don't save if required fields are missing
      return
    }

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    // Set new timeout for debounced save (1 second delay)
    saveTimeoutRef.current = setTimeout(() => {
      handleSave(null, true) // Pass true to indicate auto-save
    }, 1000)

    // Cleanup timeout on unmount or when privacySettings changes
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privacySettings, isLoadingSettings])

  const handleToggle = (settingKey) => {
    setPrivacySettings((prev) => ({
      ...prev,
      [settingKey]: !prev[settingKey],
    }))
  }

  const handleDropdownChange = (settingKey, value) => {
    setPrivacySettings((prev) => ({
      ...prev,
      [settingKey]: value,
    }))
  }

  const handleSave = async (e, isAutoSave = false) => {
    // Prevent default form submission if triggered from form
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }


    // Validate that all required dropdown fields have values
    const requiredFields = [
      { key: "whoCanChatDirectly", name: t("privacySettings.dropdowns.whoCanChatDirectly.label") },
      { key: "whoCanSeeMyPosts", name: t("privacySettings.dropdowns.whoCanSeeMyPosts.label") },
      { key: "whoCanSendFollowRequests", name: t("privacySettings.dropdowns.whoCanSendFollowRequests.label") },
      { key: "whoCanFindMeOnSearch", name: t("privacySettings.dropdowns.whoCanFindMeOnSearch.label") },
      { key: "whoCanSeeMyBuddies", name: t("privacySettings.dropdowns.whoCanSeeMyBuddies.label") },
    ]

    const missingFields = requiredFields.filter(
      (field) => !privacySettings[field.key] || privacySettings[field.key] === ""
    )


    if (missingFields.length > 0) {
      // Only show error message for manual saves, not auto-saves
      if (!isAutoSave) {
        setSaveStatus({
          type: "error",
          message: t("privacySettings.status.missingFields", {
            fields: missingFields.map((f) => f.name).join(", "),
          }),
        })
        setTimeout(() => {
          setSaveStatus(null)
        }, 5000)
      }
      return
    }

    setIsLoading(true)
    // Only show status messages for manual saves
    if (!isAutoSave) {
      setSaveStatus(null)
    }

    try {
      // Ensure booleans are actual booleans, not strings
      const payload = {
        show_last_time_seen: Boolean(privacySettings.showLastTimeSeen),
        show_read_receipts: Boolean(privacySettings.showReadReceipt),
        allow_others_to_call: Boolean(privacySettings.acceptAudioVideoCalls),
        who_can_send_messages: privacySettings.whoCanChatDirectly,
        who_can_see_my_posts: privacySettings.whoCanSeeMyPosts,
        who_can_send_me_follow_requests: privacySettings.whoCanSendFollowRequests,
        who_can_find_me_on_search: privacySettings.whoCanFindMeOnSearch,
        who_can_see_my_buddies: privacySettings.whoCanSeeMyBuddies,
      }


      const response = await api.put("/api/v1/privacy-settings", payload)


      // Only show success message for manual saves
      if (!isAutoSave) {
        setSaveStatus({ type: "success" })
        setTimeout(() => {
          setSaveStatus(null)
        }, 3000)
      }
      
      // Reload settings after successful save to ensure UI is in sync
      // Skip reload for auto-save to avoid unnecessary API calls and state updates
      if (!isAutoSave) {
        try {
          const updatedResponse = await api.get("/api/v1/privacy-settings")
          if (updatedResponse.data) {
            const settings = updatedResponse.data
            setPrivacySettings({
              showLastTimeSeen: settings.show_last_time_seen ?? true,
              showReadReceipt: settings.show_read_receipts ?? true,
              acceptAudioVideoCalls: settings.allow_others_to_call ?? true,
              whoCanChatDirectly: settings.who_can_send_messages || "",
              whoCanSeeMyPosts: settings.who_can_see_my_posts || "",
              whoCanSendFollowRequests: settings.who_can_send_me_follow_requests || "",
              whoCanFindMeOnSearch: settings.who_can_find_me_on_search || "",
              whoCanSeeMyBuddies: settings.who_can_see_my_buddies || "",
            })
          }
        } catch (reloadError) {
          console.error("Error reloading settings:", reloadError)
        }
      }
    } catch (error) {
      console.error("Error saving privacy settings:", error)
      console.error("Error response:", error.response?.data)
      
      // Only show error messages for manual saves
      if (!isAutoSave) {
        // Handle validation errors
        let errorMessage = error.message
        if (error.response?.data) {
          if (error.response.data.message) {
            errorMessage = error.response.data.message
          } else if (error.response.data.errors) {
            // Laravel validation errors
            const validationErrors = Object.values(error.response.data.errors).flat()
            errorMessage = validationErrors.join(", ")
          }
        }

        setSaveStatus({
          type: "error",
          message: errorMessage,
        })
        setTimeout(() => {
          setSaveStatus(null)
        }, 5000)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    navigate(`/dashboard/${userType}/account`)
  }

  const handleShowBlockedUsers = () => {
    navigate("/social/blocked-users")
  }

  return (
    <div
      className={`privacysettings-container ${isRTL ? "rtl" : ""}`}
      dir={i18n.dir()}
    >
      <main className="privacysettings-main">
        {isLoadingSettings && (
          <div className="privacysettings-loading-container">
            <div className="privacysettings-loading-spinner"></div>
            <p className="privacysettings-loading-text">
              {t("privacySettings.loading")}
            </p>
          </div>
        )}

        {!isLoadingSettings && (
          <>
            <div className="privacysettings-section">
              <h3 className="privacysettings-section-title">
                {t("privacySettings.sections.chat")}
              </h3>

              {chatToggleItems.map((item) => (
                <div className="privacysettings-toggle-item" key={item.key}>
                  <div className="privacysettings-toggle-header">
                    <div className="privacysettings-toggle-label">{item.label}</div>

                    <button
                      onClick={() => handleToggle(item.key)}
                      className={`privacysettings-toggle-switch ${
                        privacySettings[item.key] ? "active" : ""
                      }`}
                    >
                      <div className="privacysettings-toggle-knob" />
                    </button>
                  </div>
                  <div className="privacysettings-toggle-description">
                    {item.description}
                  </div>
                </div>
              ))}
            </div>

            <div className="privacysettings-section">
              <h3 className="privacysettings-section-title">
                {t("privacySettings.sections.privacy")}
              </h3>

              {dropdownItems.map((item) => (
                <div className="privacysettings-dropdown-item" key={item.key}>
                  <div className="privacysettings-dropdown-label">{item.label}</div>
                  <div className="privacysettings-dropdown-description">
                    {item.description}
                  </div>
                  <div className="privacysettings-dropdown-container">
                    <select
                      value={privacySettings[item.key]}
                      onChange={(e) => handleDropdownChange(item.key, e.target.value)}
                      className="privacysettings-dropdown"
                    >
                      <option value="" disabled>
                        {t("privacySettings.dropdownPlaceholder")}
                      </option>
                      {privacyOptions
                        .filter((option) => {
                          // Exclude "friends" option for "whoCanSendFollowRequests" as it's not logical
                          if (item.key === "whoCanSendFollowRequests" && option.value === "friends") {
                            return false
                          }
                          return true
                        })
                        .map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                    </select>
                    <div className="privacysettings-dropdown-icon">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#9CA3AF"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="privacysettings-actions">
              {saveStatus?.type && (
                <div
                  className={`privacysettings-save-message ${
                    saveStatus.type === "error" ? "error" : ""
                  }`}
                >
                  {saveStatus.type === "success"
                    ? t("privacySettings.status.success")
                    : saveStatus.message ||
                      t("privacySettings.status.error")}
                </div>
              )}

 
            </div>
            <div className="privacysettings-blocked-row">
              <button
                type="button"
                className="privacysettings-blocked-btn"
                onClick={handleShowBlockedUsers}
              >
                {t("privacySettings.actions.blockedUsers")}
              </button>
              <p className="privacysettings-blocked-description">
                {t("privacySettings.actions.blockedUsersDescription")}
              </p>
            </div>
          </>
        )}
      </main>
    </div>
  )
}

const PrivacySettings = () => {
  const navigate = useNavigate()
  const { userType } = useParams()

  return (
    <div className="privacysettings-page">
      <Navbar userType={userType} />
      <PrivacySettingsContent />
    </div>
  )
}

export default PrivacySettings
export { PrivacySettingsContent }
